<template>
  <div class="flex items-center gap-2">
    <label :for="uuid" class="text-sm font-medium text-gray-700 min-w-[40px]">
      {{ label }}
    </label>

    <Popover :open="isPopoverOpen" @update:open="val => isPopoverOpen = val">
      <PopoverTrigger as-child>
        <Button
          :id="uuid"
          variant="outline"
          class="w-[180px] justify-start text-left font-normal"
          @click="isPopoverOpen = true"
        >
          <CalendarIcon class="mr-2 h-4 w-4" />
          <span>{{ formattedGerman || 'Datum wählen' }}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent class="w-auto p-0 z-[100001]" data-callout-popup>
        <div class="calendar-wrapper">
          <Calendar class="timin-calendar"
            v-model="selectedDate"
            :min-value="minDate"
            :max-value="maxDate"
            :fixed-weeks="true"
            locale="de"
            @update:model-value="handleDateSelect"
          />
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-vue-next";
import { parseDate } from "@internationalized/date";
import { isoToGerman, germanToISO } from "./planner.js";

const props = defineProps({
  label: { type: String, required: true },
  modelValue: { type: String, default: "" },
  min: { type: String },
  max: { type: String },
});

const emit = defineEmits(["datechange"]);
const uuid = `date-picker-${Math.random().toString(36).slice(2, 9)}`;

// Local state for the Popover visibility
const isPopoverOpen = ref(false);

function germanToCalendarDate(value) {
  if (!value) return undefined;
  const iso = germanToISO(value);
  return iso ? parseDate(iso) : undefined;
}

function calendarDateToGerman(date) {
  return date ? isoToGerman(date.toString()) : "";
}

const selectedDate = ref(germanToCalendarDate(props.modelValue));

// When a date is picked, emit the change AND close the popover
function handleDateSelect(val) {
  emit("datechange", calendarDateToGerman(val));
  isPopoverOpen.value = false; // This closes the picker immediately
}

watch(() => props.modelValue, (val) => {
  selectedDate.value = germanToCalendarDate(val);
});

const formattedGerman = computed(() => calendarDateToGerman(selectedDate.value));
const minDate = computed(() => germanToCalendarDate(props.min));
const maxDate = computed(() => germanToCalendarDate(props.max));
</script>

<style scoped>
.calendar-wrapper {
  /* This matches the standard height of a 6-row calendar month */
  height: 350px; 
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Target the internal calendar grid. 
   Shadcn/Radix-vue components usually render a table or a grid.
   This ensures the "body" of the calendar grows to fill the 350px.
*/
:deep([data-radix-calendar-grid]) {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Alternatively, force the week rows to have a fixed height if the above selector differs */
:deep(.rdp-tbody), :deep(tbody) {
  height: 280px; /* Adjust based on your specific font/padding */
}

/* Optional: style the calendar internal if needed */
:deep(.v-calendar) {
  border: none;
}
</style>